class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  let revision = 0;
  let restoreStarted = false;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => { revision += 1; });
  }

  async function restoreCart(event = {}) {
    const historyNavigation = window.performance?.getEntriesByType?.('navigation')?.some((entry) => entry.type === 'back_forward');
    if (!event.persisted && !historyNavigation) return;
    if (restoreStarted && !event.persisted) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    restoreStarted = true;
    const requestRevision = ++revision;
    const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) || `${window.Shopify?.routes?.root || '/'}cart`;
    const options = { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } };

    try {
      const responses = await Promise.all([
        fetch(`${cartUrl}.js`, options),
        fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`, { ...options, headers: { Accept: 'text/html' } }),
      ]);
      if (responses.some((response) => !response.ok)) throw new Error('Cart refresh request failed');
      const [cart, sections] = await Promise.all(responses.map((response) => response.json()));
      if (!Number.isInteger(cart.item_count) || cart.item_count < 0 || !Array.isArray(cart.items)) {
        throw new Error('Cart refresh data is incomplete');
      }
      if (typeof sections['cart-drawer'] !== 'string' || typeof sections['cart-icon-bubble'] !== 'string') {
        throw new Error('Cart refresh sections are incomplete');
      }
      const parser = new DOMParser();
      const drawerSource = parser.parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
      const bubbleSource = parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      const drawerTarget = drawer.querySelector('#CartDrawer');
      const bubbleTarget = document.getElementById('cart-icon-bubble');
      if (!drawerSource?.querySelector('#CartDrawer-Overlay') || !drawerTarget || (bubbleTarget && !bubbleSource)) {
        throw new Error('Cart refresh markup is incomplete');
      }
      if (requestRevision !== revision || !drawer.isConnected) return;

      drawer.close();
      drawerTarget.replaceChildren(...drawerSource.childNodes);
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      drawer.querySelector('#CartDrawer-Overlay').addEventListener('click', drawer.close.bind(drawer));
      if (bubbleTarget) bubbleTarget.replaceChildren(...bubbleSource.childNodes);
      document.querySelectorAll('[data-jsk-mobile-cart-count]').forEach((badge) => {
        badge.textContent = String(cart.item_count);
        badge.setAttribute('aria-label', `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart`);
      });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
      }
    } catch (error) {
      if (requestRevision === revision) restoreStarted = false;
      console.warn('Unable to refresh the cart after returning to this page.', error);
    }
  }

  window.addEventListener('pageshow', restoreCart);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => restoreCart(), { once: true });
  } else {
    restoreCart();
  }
})();
